/**
 * 分镜列表 ⇄ FormState 平行数组的**单点聚合层**(两态收敛批,2026-07-26)。
 *
 * ═══ 为什么必须有这层 ═══════════════════════════════════════════════════════════
 * 契约 v2 的每镜数据散在 FormState 的 **22 条平行数组**里(基准数组 = panelScenes,
 * 它的长度就是镜数)。此前页面的 `writeShots` 只重写其中 9 条 —— 剩下 13 条
 * (运镜/景别/镜速/光/承接/heroMoments/subjectRefs/voiceEmotions/factRefs…)在
 * **删镜时不跟着删**:数组留在旧长度,ai 侧 `_arr(x, n)` 按新镜数截断,删掉镜 2 之后
 * 镜 3 顶上镜 2 的运镜/景别 —— 全片镜头语言整体错位一格,而且没有任何报错。
 * (此前没炸穿只因这些字段 UI 不可见;镜头语言行一旦可编辑,错位立刻可见。)
 *
 * 修法 = 老病的老方子(panelBeats 在 M2 就是这么治的):**每镜数据全量随镜携带**,
 * 增/删/插统一走本层,一次重写全部平行数组,永不错位。
 *
 * ★空数组语义:可选数组「全空 → 写 null」(保持「没这份数据」与「有但为空」的区别;
 * mergeStoryboard/后端 putIf 都是按 null 跳过的)。
 */
import type { FormState } from "./videoCreateTypes";
import type { PanelShotData } from "./panelShot";

/**
 * 新建镜的**种子秒数**(2026-09-13 A5②)。此前取 `form.durationSec`,但高级选项里的
 * 「单镜秒数」输入框已随 A5 退役 —— 用户编辑不到它了,`durationSec` 只剩默认值(5,见
 * VIDEO_DEFAULT_CLIP_SECONDS)当每镜兜底/裸发回落。所以这里改用常量,把新镜种子秒数与那个
 * 摸不到的字段解耦:每镜真时长由 AI 编排(panelDurations),用户手动加镜/插镜就从 5s 起。
 */
const SEED_SHOT_SEC = 5;

const at = <T>(arr: readonly T[] | null | undefined, i: number): T | undefined =>
  arr ? arr[i] : undefined;

/** FormState 平行数组 → 每镜自包含的 PanelShotData 列表(基准 = panelScenes)。 */
export function shotsFromForm(form: FormState): PanelShotData[] {
  return (form.panelScenes ?? []).map((scene, i) => ({
    scene,
    motion: form.panelMotions?.[i] ?? "",
    duration: form.panelDurations?.[i] ?? SEED_SHOT_SEC, // A5②:durationSec 退役 → 常量种子秒数
    voiceover: form.voiceovers?.[i] ?? "",
    negativePrompt: form.negativePrompts?.[i] ?? form.negativePrompt ?? "",
    firstFrameKey: form.panelFirstFrameKeys?.[i] || undefined,
    firstFrameUrl: form.panelFirstFrameUrls?.[i] || undefined,
    previewUrl: form.panelPreviewUrls?.[i] || undefined,
    previewKey: form.panelPreviewKeys?.[i] || undefined,
    shotMode: form.shotMode?.[i] || undefined,
    beat: form.panelBeats?.[i] ?? "",
    // ── 镜头语言(受控枚举;导演产,分镜页可改)──
    shotSize: at(form.shotSizes, i) ?? "",
    cameraMovement: at(form.cameraMovements, i) ?? "",
    cameraSpeed: at(form.cameraSpeeds, i) ?? "",
    lightingKey: at(form.lightingKeys, i) ?? "",
    shotContinuity: at(form.shotContinuities, i) ?? "",
    // ── 其余逐镜字段(UI 只读/不显,但必须随镜增删同步)──
    subjectRef: at(form.panelSubjectRefs, i) ?? "",
    voiceEmotion: at(form.voiceEmotions, i) ?? "",
    shotIntent: at(form.shotIntents, i) ?? "",
    narrativeRole: at(form.narrativeRoles, i) ?? "",
    heroMoment: at(form.heroMoments, i) ?? false,
    sceneId: at(form.sceneIds, i) ?? "",
    factRef: at(form.factRefs, i) ?? [],
    factVisual: at(form.factVisuals, i) ?? "",
    speechMode: at(form.speechModes, i) ?? "",
    references: at(form.references, i) ?? [],
  }));
}

/** 可选数组:全空 → null(「没这份数据」),否则整列写回。 */
function optional<T>(vals: T[], isEmpty: (v: T) => boolean): T[] | null {
  return vals.some((v) => !isEmpty(v)) ? vals : null;
}
const emptyStr = (v: string) => !(v ?? "").trim();

/** PanelShotData 列表 → FormState 补丁(一次重写**全部** 22 条平行数组)。 */
export function formPatchFromShots(next: PanelShotData[]): Partial<FormState> {
  return {
    panelScenes: next.map((x) => x.scene),
    panelMotions: next.map((x) => x.motion),
    panelDurations: next.map((x) => x.duration),
    voiceovers: next.map((x) => x.voiceover),
    negativePrompts: next.map((x) => x.negativePrompt),
    panelFirstFrameKeys: next.map((x) => x.firstFrameKey ?? ""),
    panelFirstFrameUrls: next.map((x) => x.firstFrameUrl ?? ""),
    panelPreviewUrls: next.map((x) => x.previewUrl ?? ""),
    panelPreviewKeys: next.map((x) => x.previewKey ?? ""),
    shotMode: next.map((x) => x.shotMode ?? "I2V"),
    panelBeats: optional(
      next.map((x) => x.beat ?? ""),
      emptyStr
    ),
    shotSizes: optional(
      next.map((x) => x.shotSize ?? ""),
      emptyStr
    ),
    cameraMovements: optional(
      next.map((x) => x.cameraMovement ?? ""),
      emptyStr
    ),
    cameraSpeeds: optional(
      next.map((x) => x.cameraSpeed ?? ""),
      emptyStr
    ),
    lightingKeys: optional(
      next.map((x) => x.lightingKey ?? ""),
      emptyStr
    ),
    shotContinuities: optional(
      next.map((x) => x.shotContinuity ?? ""),
      emptyStr
    ),
    panelSubjectRefs: optional(
      next.map((x) => x.subjectRef ?? ""),
      emptyStr
    ),
    voiceEmotions: optional(
      next.map((x) => x.voiceEmotion ?? ""),
      emptyStr
    ),
    shotIntents: optional(
      next.map((x) => x.shotIntent ?? ""),
      emptyStr
    ),
    narrativeRoles: optional(
      next.map((x) => x.narrativeRole ?? ""),
      emptyStr
    ),
    heroMoments: optional(
      next.map((x) => x.heroMoment ?? false),
      (v) => !v
    ),
    sceneIds: optional(
      next.map((x) => x.sceneId ?? ""),
      emptyStr
    ),
    factRefs: optional(
      next.map((x) => x.factRef ?? []),
      (v) => v.length === 0
    ),
    factVisuals: optional(
      next.map((x) => x.factVisual ?? ""),
      emptyStr
    ),
    speechModes: optional(
      next.map((x) => x.speechMode ?? ""),
      emptyStr
    ),
    references: optional(
      next.map((x) => x.references ?? []),
      (v) => v.length === 0
    ),
  };
}

/** 新空镜(加/插镜共用;继承请求级默认)。 */
export function emptyShot(form: FormState): PanelShotData {
  return {
    scene: "",
    motion: "",
    duration: SEED_SHOT_SEC, // A5②:durationSec 退役 → 常量种子秒数(不再读 form.durationSec)
    voiceover: "",
    negativePrompt: form.negativePrompt ?? "",
  };
}

/** 在 index 处插一镜(index = 新镜落位;length = 尾插)。 */
export function insertShotAt(
  shots: PanelShotData[],
  index: number,
  shot: PanelShotData
): PanelShotData[] {
  const i = Math.max(0, Math.min(index, shots.length));
  return [...shots.slice(0, i), shot, ...shots.slice(i)];
}
